/**
 * Procedural vector symbols for plan objects (pure display-list output).
 * Symbols are PRESENTATION derived from the object row — never authoritative
 * geometry — and reuse the existing ScenePrimitive vocabulary so the same
 * definitions serve Canvas today and SVG export / 3D footprints later.
 */
import type { PlanModel, PlanObject, Point } from "./model";
import { objectFootprint, objectPose, objectRunLength } from "./objects";
import type { ScenePrimitive, StyleToken } from "./scene";
import { roundPoint } from "./vector";

/** Local object frame: x along width (run direction), y along depth; unit = fraction of size. */
interface Frame {
  pt: (lx: number, ly: number) => Point;
  w: number;
  d: number;
}

function frameOf(model: PlanModel, o: PlanObject): Frame {
  const pose = objectPose(model, o);
  const w = o.geometryType === "point" ? o.width : objectRunLength(o);
  const d = o.depth;
  const cos = Math.cos(pose.rotationRad);
  const sin = Math.sin(pose.rotationRad);
  return {
    w,
    d,
    pt: (lx: number, ly: number) =>
      roundPoint({ x: pose.center.x + lx * w * cos - ly * d * sin, y: pose.center.y + lx * w * sin + ly * d * cos }),
  };
}

function outline(f: Frame, style: StyleToken): ScenePrimitive[] {
  const pts = [f.pt(-0.5, -0.5), f.pt(0.5, -0.5), f.pt(0.5, 0.5), f.pt(-0.5, 0.5)];
  return [
    { type: "polygon", points: pts, style: "object-fill" },
    ...pts.map((p, i) => ({ type: "line" as const, from: p, to: pts[(i + 1) % 4], style, widthPx: 1.5 })),
  ];
}

function line(f: Frame, a: [number, number], b: [number, number], style: StyleToken, widthPx = 1): ScenePrimitive {
  return { type: "line", from: f.pt(a[0], a[1]), to: f.pt(b[0], b[1]), style, widthPx };
}

function circle(f: Frame, cx: number, cy: number, radiusPu: number, style: StyleToken): ScenePrimitive {
  return { type: "arc", center: f.pt(cx, cy), radiusPu, startRad: 0, endRad: Math.PI * 2 - 1e-6, ccw: true, style, widthPx: 1 };
}

/** Build the symbol for one object. `style` reflects selection/hover state. */
export function objectSymbol(
  model: PlanModel,
  o: PlanObject & { stepCount?: number | null; upDirection?: "forward" | "back" },
  symbolKey: string,
  style: StyleToken,
): ScenePrimitive[] {
  const f = frameOf(model, o);
  const prims: ScenePrimitive[] = [];
  switch (symbolKey) {
    case "toilet": {
      // tank against the back (negative y = back), elliptical bowl in front
      prims.push(...outlineRect(f, -0.5, -0.5, 0.5, -0.15, style));
      prims.push(circle(f, 0, 0.2, Math.min(f.w, f.d * 0.55) * 0.42, style));
      break;
    }
    case "sink":
    case "vanity": {
      prims.push(...outline(f, style));
      prims.push(circle(f, symbolKey === "vanity" ? -0.2 : 0, 0, Math.min(f.w, f.d) * 0.28, style));
      if (symbolKey === "vanity" && f.w > f.d * 1.4) prims.push(circle(f, 0.25, 0, Math.min(f.w, f.d) * 0.16, style));
      break;
    }
    case "tub": {
      prims.push(...outline(f, style));
      prims.push(...outlineRect(f, -0.38, -0.3, 0.38, 0.3, style));
      break;
    }
    case "shower": {
      prims.push(...outline(f, style));
      prims.push(line(f, [-0.5, -0.5], [0.5, 0.5], style));
      prims.push(line(f, [0.5, -0.5], [-0.5, 0.5], style));
      prims.push(circle(f, 0, 0, Math.min(f.w, f.d) * 0.08, style));
      break;
    }
    case "range": {
      prims.push(...outline(f, style));
      const r = Math.min(f.w, f.d) * 0.14;
      for (const [cx, cy] of [[-0.25, -0.2], [0.25, -0.2], [-0.25, 0.2], [0.25, 0.2]] as const) prims.push(circle(f, cx, cy, r, style));
      break;
    }
    case "refrigerator": {
      prims.push(...outline(f, style));
      prims.push(line(f, [-0.1, -0.5], [-0.1, 0.5], style));
      prims.push(line(f, [-0.5, 0.5], [-0.45, 0.62], style));
      break;
    }
    case "dishwasher": {
      prims.push(...outline(f, style));
      prims.push(line(f, [-0.5, -0.25], [0.5, -0.25], style));
      break;
    }
    case "microwave": {
      prims.push(...outline(f, style));
      prims.push(...outlineRect(f, -0.4, -0.28, 0.15, 0.28, style));
      break;
    }
    case "water-heater": {
      prims.push(circle(f, 0, 0, Math.min(f.w, f.d) / 2, style));
      prims.push({ type: "text", at: f.pt(0, 0), text: "WH", style: "object-label", sizePx: 9 });
      break;
    }
    case "light": {
      const r = Math.min(f.w, f.d) / 2;
      prims.push(circle(f, 0, 0, r, style));
      prims.push(line(f, [-0.35, -0.35], [0.35, 0.35], style));
      prims.push(line(f, [0.35, -0.35], [-0.35, 0.35], style));
      break;
    }
    case "ceiling-fan": {
      const r = Math.min(f.w, f.d) / 2;
      prims.push(circle(f, 0, 0, r * 0.25, style));
      for (const [a, b] of [[[0.12, 0], [0.5, 0]], [[-0.12, 0], [-0.5, 0]], [[0, 0.12], [0, 0.5]], [[0, -0.12], [0, -0.5]]] as const) {
        prims.push(line(f, a as [number, number], b as [number, number], style, 2));
      }
      break;
    }
    case "outlet": {
      prims.push(circle(f, 0, 0, Math.max(f.w, f.d) / 2, style));
      prims.push(line(f, [-0.6, 0], [0.6, 0], style));
      break;
    }
    case "switch": {
      prims.push({ type: "text", at: f.pt(0, 0), text: "S", style, sizePx: 11 });
      break;
    }
    case "cabinet-run": {
      prims.push(...outline(f, style));
      // hatch marks along the run
      const step = Math.max(1, Math.round(f.w / (24 * 2032))) > 0 ? 24 * 2032 : f.w;
      for (let x = -f.w / 2 + step; x < f.w / 2; x += step) {
        prims.push({ type: "line", from: f.pt(x / f.w, -0.5), to: f.pt(x / f.w, 0.5), style, widthPx: 0.75 });
      }
      break;
    }
    case "upper-cabinet-run": {
      const pts = [f.pt(-0.5, -0.5), f.pt(0.5, -0.5), f.pt(0.5, 0.5), f.pt(-0.5, 0.5)];
      for (let i = 0; i < 4; i++) prims.push({ type: "line", from: pts[i], to: pts[(i + 1) % 4], style, widthPx: 1, dash: [5, 4] });
      break;
    }
    case "countertop": {
      const pts = [f.pt(-0.5, -0.5), f.pt(0.5, -0.5), f.pt(0.5, 0.5), f.pt(-0.5, 0.5)];
      prims.push({ type: "polygon", points: pts, style: "object-counter" });
      for (let i = 0; i < 4; i++) prims.push({ type: "line", from: pts[i], to: pts[(i + 1) % 4], style, widthPx: 1.25 });
      break;
    }
    case "stairs": {
      // The conventional plan symbol: the run outlined, treads drawn across
      // it, and an arrow that says UP. Nobody draws individual treads by
      // hand — the count comes from the staircase's own figures, and when
      // nobody has measured yet a readable default is drawn rather than a
      // fabricated number of steps (spec §60I).
      prims.push(...outline(f, style));
      const steps = Math.max(2, Math.min(24, o.stepCount ?? 13));
      for (let i = 1; i < steps; i += 1) {
        const x = -0.5 + i / steps;
        prims.push({ type: "line", from: f.pt(x, -0.5), to: f.pt(x, 0.5), style, widthPx: 0.75 });
      }
      // UP arrow along the run, pointing the way the stair climbs.
      const forward = o.upDirection !== "back";
      const tail = f.pt(forward ? -0.38 : 0.38, 0);
      const head = f.pt(forward ? 0.38 : -0.38, 0);
      const barbA = f.pt(forward ? 0.24 : -0.24, -0.16);
      const barbB = f.pt(forward ? 0.24 : -0.24, 0.16);
      prims.push({ type: "line", from: tail, to: head, style, widthPx: 1.5 });
      prims.push({ type: "line", from: head, to: barbA, style, widthPx: 1.5 });
      prims.push({ type: "line", from: head, to: barbB, style, widthPx: 1.5 });
      prims.push({ type: "text", at: tail, text: "UP", style: "label-strong", sizePx: 10, offsetPx: [0, -10] });
      break;
    }
    // ---- Cabinet modules (spec §61G). A module is drawn as the box it is,
    // with the marks that tell one apart at a glance: drawer fronts, a sink
    // bowl, a dashed upper, an angled or blind corner.
    case "base-cabinet": {
      prims.push(...outline(f, style));
      prims.push(line(f, [-0.42, -0.32], [0.42, -0.32], style, 0.75));
      break;
    }
    case "drawer-base": {
      prims.push(...outline(f, style));
      for (const y of [-0.2, 0.05, 0.3]) prims.push(line(f, [-0.42, y], [0.42, y], style, 0.75));
      break;
    }
    case "sink-base": {
      prims.push(...outline(f, style));
      prims.push(...outlineRect(f, -0.36, -0.3, 0.36, 0.3, style));
      prims.push(circle(f, 0, 0, Math.max(1, Math.round(Math.min(f.w, f.d) * 0.08)), style));
      break;
    }
    case "wall-cabinet": {
      const pts = [f.pt(-0.5, -0.5), f.pt(0.5, -0.5), f.pt(0.5, 0.5), f.pt(-0.5, 0.5)];
      for (let i = 0; i < 4; i++) prims.push({ type: "line", from: pts[i], to: pts[(i + 1) % 4], style, widthPx: 1, dash: [5, 4] });
      prims.push({ type: "line", from: f.pt(0, -0.5), to: f.pt(0, 0.5), style, widthPx: 0.75, dash: [5, 4] });
      break;
    }
    case "tall-cabinet": {
      prims.push(...outline(f, style));
      prims.push(...outlineRect(f, -0.42, -0.38, 0.42, 0.38, style));
      prims.push(line(f, [0, -0.38], [0, 0.38], style, 0.75));
      break;
    }
    case "bath-cabinet": {
      prims.push(...outline(f, style));
      prims.push(line(f, [-0.42, -0.15], [0.42, -0.15], style, 0.75));
      prims.push(line(f, [-0.42, 0.15], [0.42, 0.15], style, 0.75));
      break;
    }
    case "corner-base":
    case "corner-wall": {
      // The corner shape is chosen from a picture and DRAWN as that shape
      // (spec §61G): a diagonal front, a blind run into the corner from one
      // side, or a square box with a round shelf.
      const dashed = symbolKey === "corner-wall";
      const shape = o.config ?? "diagonal";
      const box = [f.pt(-0.5, -0.5), f.pt(0.5, -0.5), f.pt(0.5, 0.5), f.pt(-0.5, 0.5)];
      for (let i = 0; i < 4; i++) {
        prims.push({ type: "line", from: box[i], to: box[(i + 1) % 4], style, widthPx: 1, ...(dashed ? { dash: [5, 4] as [number, number] } : {}) });
      }
      if (shape === "diagonal") {
        prims.push({ type: "polygon", points: [f.pt(-0.5, 0.5), f.pt(0.5, -0.5), f.pt(0.5, 0.5)], style: "object-fill" });
        prims.push(line(f, [-0.5, 0.5], [0.5, -0.5], style, 1.5));
      } else if (shape === "lazy-susan") {
        prims.push(circle(f, 0, 0, Math.max(1, Math.round(Math.min(f.w, f.d) * 0.34)), style));
        prims.push(line(f, [-0.5, 0.5], [0.5, -0.5], style, 0.75));
      } else {
        // Blind: the unreachable part of the box is hatched, on the side the
        // cabinet runs in from.
        const left = shape === "blind-left";
        const x0 = left ? -0.5 : 0.16;
        const x1 = left ? -0.16 : 0.5;
        prims.push({ type: "polygon", points: [f.pt(x0, -0.5), f.pt(x1, -0.5), f.pt(x1, 0.5), f.pt(x0, 0.5)], style: "object-fill" });
        prims.push(line(f, [left ? -0.16 : 0.16, -0.5], [left ? -0.16 : 0.16, 0.5], style, 1.25));
      }
      break;
    }

    // ---- Laundry (spec §61F).
    case "washer":
    case "dryer": {
      prims.push(...outline(f, style));
      prims.push(circle(f, 0, 0.05, Math.max(1, Math.round(Math.min(f.w, f.d) * 0.28)), style));
      prims.push(line(f, [-0.42, -0.34], [0.42, -0.34], style, 0.75));
      if (symbolKey === "dryer") {
        // The vent stub: the thing that decides whether this location works.
        prims.push(line(f, [0.3, -0.5], [0.3, -0.62], style, 1.25));
      }
      break;
    }
    case "laundry-stacked": {
      prims.push(...outline(f, style));
      prims.push(circle(f, 0, 0.16, Math.max(1, Math.round(Math.min(f.w, f.d) * 0.22)), style));
      prims.push(circle(f, 0, -0.2, Math.max(1, Math.round(Math.min(f.w, f.d) * 0.22)), style));
      prims.push(line(f, [-0.5, -0.02], [0.5, -0.02], style, 1.25));
      break;
    }

    // ---- Furniture (spec §61J): recognisable at a glance, nothing more.
    case "sofa": {
      prims.push(...outline(f, style));
      prims.push(...outlineRect(f, -0.44, -0.44, 0.44, -0.18, style));
      prims.push(line(f, [-0.18, -0.18], [-0.18, 0.44], style, 0.75));
      prims.push(line(f, [0.18, -0.18], [0.18, 0.44], style, 0.75));
      break;
    }
    case "sectional": {
      prims.push(...outline(f, style));
      prims.push(...outlineRect(f, -0.46, -0.46, 0.46, -0.2, style));
      prims.push(...outlineRect(f, -0.46, -0.2, -0.2, 0.46, style));
      break;
    }
    case "chair": {
      prims.push(...outline(f, style));
      prims.push(line(f, [-0.42, -0.34], [0.42, -0.34], style, 1.25));
      break;
    }
    case "table": {
      prims.push(...outline(f, style));
      prims.push(...outlineRect(f, -0.4, -0.34, 0.4, 0.34, style));
      break;
    }
    case "bed": {
      prims.push(...outline(f, style));
      // headboard, then the pillows that say which end is which
      prims.push(line(f, [-0.5, -0.44], [0.5, -0.44], style, 1.5));
      prims.push(...outlineRect(f, -0.44, -0.4, -0.06, -0.24, style));
      prims.push(...outlineRect(f, 0.06, -0.4, 0.44, -0.24, style));
      prims.push(line(f, [-0.5, 0.1], [0.5, 0.1], style, 0.75));
      break;
    }
    case "dresser":
    case "nightstand": {
      prims.push(...outline(f, style));
      prims.push(line(f, [-0.42, 0], [0.42, 0], style, 0.75));
      break;
    }
    case "desk": {
      prims.push(...outline(f, style));
      prims.push(line(f, [-0.42, 0.2], [0.42, 0.2], style, 0.75));
      prims.push(line(f, [0.1, -0.5], [0.1, 0.2], style, 0.75));
      break;
    }
    case "stool": {
      prims.push(circle(f, 0, 0, Math.max(1, Math.round(Math.min(f.w, f.d) * 0.45)), style));
      break;
    }

    default: {
      prims.push(...outline(f, style));
    }
  }
  return prims;
}

function outlineRect(f: Frame, x0: number, y0: number, x1: number, y1: number, style: StyleToken): ScenePrimitive[] {
  const pts = [f.pt(x0, y0), f.pt(x1, y0), f.pt(x1, y1), f.pt(x0, y1)];
  return pts.map((p, i) => ({ type: "line" as const, from: p, to: pts[(i + 1) % 4], style, widthPx: 1.25 }));
}

export { objectFootprint };
